import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Container,
  Typography,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import {
  LocationOn,
  Phone,
  LocalOffer,
  Visibility,
  AccessTime,
  Share as ShareIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetStoreByIdQuery,
  useIncrementStoreShareMutation,
  useIncrementStoreViewMutation,
} from "../../features/stores/storesApiSlice";
import {
  getCategoryConfig,
  formatFloor,
  formatPhoneNumber,
} from "../../utils/storeHelpers";
import ScrollIndicator, {
  BottomFadeOverlay,
} from "../../components/common/ScrollIndicator";
import {
  DetailHero,
  DetailSkeleton,
  DetailErrorState,
  DetailShareBar,
  DetailFloatingButton,
  useDetailShare,
  useScrollEffect,
} from "../../components/Details";

const StoreDetails: React.FC = () => {
  const navigate = useNavigate();
  const { storeId } = useParams<{ storeId: string }>();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showShareBar, setShowShareBar] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);

  // Fetch store details
  const {
    data: store,
    isLoading,
    error,
  } = useGetStoreByIdQuery(storeId || "", {
    skip: !storeId,
  });

  // Track share mutation
  const [incrementShare] = useIncrementStoreShareMutation();

  // Track view mutation
  const [incrementView] = useIncrementStoreViewMutation();

  // Share functionality with localStorage tracking
  const shareHandlers = useDetailShare({
    itemId: storeId || "",
    itemType: "store",
    shareText: store
      ? `🏪 Check out this store!\n\n${store.name}\n${
          store.description || ""
        }\n\n📍 ${formatFloor(store.floor)} - ${store.shopNumber}\n${
          store.mallName ? `🏬 ${store.mallName}` : ""
        }\n\n`
      : "",
    incrementShareMutation: incrementShare,
  });

  // Increment view count once per user (localStorage tracking)
  useEffect(() => {
    if (store && storeId) {
      const viewKey = `store_viewed_${storeId}`;
      const hasViewed = localStorage.getItem(viewKey);

      if (!hasViewed) {
        // First time viewing - increment count and mark as viewed
        incrementView({ storeId, mallId: store.mallId });
        localStorage.setItem(viewKey, "true");
      }
    }
  }, [store, storeId, incrementView]);

  // Smooth scroll effect using custom hook
  useScrollEffect(contentRef, setShowShareBar);

  // Handle image carousel navigation
  const images = store?.imageUrl
    ? [store.imageUrl]
    : [store?.logoUrl || "/placeholder-store.jpg"];

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  if (isLoading) {
    return <DetailSkeleton />;
  }

  if (error || !store) {
    return (
      <DetailErrorState onBack={() => navigate(-1)} message="Store not found" />
    );
  }

  const categoryConfig = getCategoryConfig(store.category);
  const Icon = categoryConfig.icon;

  // Build meta info for hero section
  const heroMetaInfo = (
    <>
      {/* Store Logo */}
      {store.logoUrl && (
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              width: { xs: 60, md: 80 },
              height: { xs: 60, md: 80 },
              bgcolor: "white",
              borderRadius: 2,
              p: 1,
              boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={store.logoUrl}
              alt={store.name}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </Box>
        </Box>
      )}

      {/* Location */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.5, md: 2 },
          mb: { xs: 1.5, md: 2 },
        }}
      >
        <Box
          sx={{
            bgcolor: "rgba(255,255,255,0.2)",
            backdropFilter: "blur(10px)",
            borderRadius: 1.5,
            p: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <LocationOn sx={{ fontSize: { xs: 18, md: 20 }, color: "white" }} />
        </Box>
        <Typography
          variant="body1"
          sx={{
            color: "white",
            fontWeight: 600,
            fontSize: { xs: "0.875rem", md: "0.938rem" },
            textShadow: "0 2px 8px rgba(0,0,0,0.3)",
          }}
        >
          {formatFloor(store.floor)} - {store.shopNumber}
        </Typography>
      </Box>

      {/* Active Offers */}
      {store.activeOffers !== undefined && store.activeOffers > 0 && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1.5, md: 2 },
            mb: { xs: 2, md: 2.5 },
          }}
        >
          <Box
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              backdropFilter: "blur(10px)",
              borderRadius: 1.5,
              p: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LocalOffer sx={{ fontSize: { xs: 18, md: 20 }, color: "white" }} />
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: "white",
              fontWeight: 600,
              fontSize: { xs: "0.875rem", md: "0.938rem" },
              textShadow: "0 2px 8px rgba(0,0,0,0.3)",
            }}
          >
            {store.activeOffers} Active{" "}
            {store.activeOffers === 1 ? "Offer" : "Offers"}
          </Typography>
        </Box>
      )}

      {/* View and Share Counts */}
      {(store.viewCount !== undefined || store.shareCount !== undefined) && (
        <Box
          sx={{
            display: "flex",
            gap: { xs: 2.5, md: 3 },
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {/* Views */}
          {store.viewCount !== undefined && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, md: 2 },
              }}
            >
              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(10px)",
                  borderRadius: 1.5,
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Visibility
                  sx={{ fontSize: { xs: 18, md: 20 }, color: "white" }}
                />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: "white",
                  fontWeight: 600,
                  fontSize: { xs: "0.875rem", md: "0.938rem" },
                  textShadow: "0 2px 8px rgba(0,0,0,0.3)",
                }}
              >
                {store.viewCount.toLocaleString()} views
              </Typography>
            </Box>
          )}

          {/* Shares */}
          {store.shareCount !== undefined && store.shareCount > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1.5, md: 2 },
              }}
            >
              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  backdropFilter: "blur(10px)",
                  borderRadius: 1.5,
                  p: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ShareIcon
                  sx={{ fontSize: { xs: 18, md: 20 }, color: "white" }}
                />
              </Box>
              <Typography
                variant="body1"
                sx={{
                  color: "white",
                  fontWeight: 600,
                  fontSize: { xs: "0.875rem", md: "0.938rem" },
                  textShadow: "0 2px 8px rgba(0,0,0,0.3)",
                }}
              >
                {store.shareCount.toLocaleString()} shares
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f7fa",
        pb: 3,
      }}
    >
      {/* Hero Section - Using DetailHero component */}
      <DetailHero
        images={images}
        currentImageIndex={currentImageIndex}
        title={store.name}
        categoryLabel={categoryConfig.label}
        categoryIcon={<Icon />}
        gradientColors={categoryConfig.gradient}
        metaInfo={heroMetaInfo}
        onBack={() => navigate(-1)}
        onPrevImage={images.length > 1 ? handlePrevImage : undefined}
        onNextImage={images.length > 1 ? handleNextImage : undefined}
      />

      {/* Main Content - Slides up over hero */}
      <Box
        ref={contentRef}
        sx={{
          position: "relative",
          zIndex: 10,
          background: "linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%)",
          borderTopLeftRadius: { xs: 20, md: 32 },
          borderTopRightRadius: { xs: 20, md: 32 },
          boxShadow: "0 -8px 32px rgba(0,0,0,0.12)",
          willChange: "transform",
          mt: { xs: -2, md: -3 },
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ pt: { xs: 3, md: 4 }, pb: 2, px: { xs: 2, sm: 3 } }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            {/* Description Section */}
            {store.description && (
              <Card
                elevation={0}
                sx={{
                  mb: 3,
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                  <Typography
                    variant="h5"
                    gutterBottom
                    sx={{ fontWeight: 700, mb: 2 }}
                  >
                    About This Store
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "text.secondary",
                      lineHeight: 1.8,
                      fontSize: "1rem",
                    }}
                  >
                    {store.description}
                  </Typography>
                </CardContent>
              </Card>
            )}

            {/* Store Details Grid */}
            <Grid container spacing={3}>
              {/* Contact Information */}
              <Grid item xs={12} md={6}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          bgcolor: "primary.main",
                          borderRadius: 2,
                          p: 1.5,
                          display: "flex",
                        }}
                      >
                        <Phone sx={{ color: "white" }} />
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Contact Details
                      </Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      {store.contactNumber && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              textTransform: "uppercase",
                              fontWeight: 600,
                              letterSpacing: 0.5,
                            }}
                          >
                            Phone Number
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, mt: 0.5 }}
                          >
                            <a
                              href={`tel:${store.contactNumber}`}
                              style={{
                                color: "#1976d2",
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                            >
                              {formatPhoneNumber(store.contactNumber)}
                            </a>
                          </Typography>
                        </Box>
                      )}
                      {store.email && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              textTransform: "uppercase",
                              fontWeight: 600,
                              letterSpacing: 0.5,
                            }}
                          >
                            Email
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, mt: 0.5 }}
                          >
                            <a
                              href={`mailto:${store.email}`}
                              style={{
                                color: "#1976d2",
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                            >
                              {store.email}
                            </a>
                          </Typography>
                        </Box>
                      )}
                      {store.website && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              textTransform: "uppercase",
                              fontWeight: 600,
                              letterSpacing: 0.5,
                            }}
                          >
                            Website
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{ fontWeight: 600, mt: 0.5 }}
                          >
                            <a
                              href={store.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: "#1976d2",
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                            >
                              Visit Website
                            </a>
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Opening Hours */}
              {store.openingHours && (
                <Grid item xs={12} md={6}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            bgcolor: "success.main",
                            borderRadius: 2,
                            p: 1.5,
                            display: "flex",
                          }}
                        >
                          <AccessTime sx={{ color: "white" }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          Opening Hours
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1.5,
                        }}
                      >
                        {Object.entries(store.openingHours).map(
                          ([day, hours]) => (
                            <Box
                              key={day}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  textTransform: "capitalize",
                                  color: "text.secondary",
                                }}
                              >
                                {day}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 600, color: "text.primary" }}
                              >
                                {hours}
                              </Typography>
                            </Box>
                          )
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Store Tags */}
              {store.tags && store.tags.length > 0 && (
                <Grid item xs={12}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ fontWeight: 700, mb: 2 }}
                      >
                        Tags
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {store.tags.map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            sx={{
                              background:
                                "linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)",
                              fontWeight: 600,
                              fontSize: "0.875rem",
                            }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Store Offers */}
              {store.offers && store.offers.length > 0 && (
                <Grid item xs={12}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Box
                            sx={{
                              bgcolor: "secondary.main",
                              borderRadius: 2,
                              p: 1.5,
                              display: "flex",
                            }}
                          >
                            <LocalOffer sx={{ color: "white" }} />
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Active Offers ({store.offers.length})
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            navigate(`/mall/${store.mallId}/offers`, {
                              state: { storeFilter: store.id },
                            })
                          }
                        >
                          View All
                        </Button>
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      <Grid container spacing={2}>
                        {store.offers.slice(0, 3).map((offer) => (
                          <Grid item xs={12} sm={6} md={4} key={offer.id}>
                            <Box
                              onClick={() =>
                                navigate(
                                  `/mall/${store.mallId}/offers/${offer.id}`
                                )
                              }
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                cursor: "pointer",
                                transition: "all 0.2s",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  boxShadow: 2,
                                  borderColor: "primary.main",
                                },
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 700,
                                  mb: 0.5,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                }}
                              >
                                {offer.title}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "primary.main", fontWeight: 600 }}
                              >
                                {offer.discountText}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Bottom Sticky Share Bar */}
      <DetailShareBar
        show={showShareBar}
        onWhatsAppShare={shareHandlers.handleWhatsAppShare}
        onCopyLink={shareHandlers.handleCopyLink}
        onClose={() => setShowShareBar(false)}
      />

      {/* Floating WhatsApp Button */}
      <DetailFloatingButton
        show={!showShareBar}
        onClick={shareHandlers.handleWhatsAppShare}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={shareHandlers.snackbar.open}
        autoHideDuration={3000}
        onClose={shareHandlers.handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={shareHandlers.handleSnackbarClose}
          severity={shareHandlers.snackbar.severity}
          sx={{
            width: "100%",
            borderRadius: 3,
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            fontWeight: 600,
          }}
        >
          {shareHandlers.snackbar.message}
        </Alert>
      </Snackbar>

      {/* Scroll Indicator for Mobile Users */}
      <ScrollIndicator color="primary.main" bottomOffset={100} />
      <BottomFadeOverlay color="#f5f7fa" height={100} />
    </Box>
  );
};

export default StoreDetails;
